import {
  ASSIGN_TO_ADDON,
  FIND_ADDON_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal } from '../../shared/components/assignment/assignmentModal';
import { AddonAssetsAssignmentList } from './addonAssetsAssignmentList';
import { AddonMediaAssignmentList } from './addonMediaAssignmentList';
import { AddonProductAssignmentList } from './addonProductAssignmentList';
import { AddonRoutinesAssignList } from './addonRoutinesAssignList';

interface Props {
  addonId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'product' | 'media' | 'routines' | 'assets';
}

export const AddonAssignmentForm = ({
  addonId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();

  const { data, isPending } = useCustomQuery({
    query: FIND_ADDON_ASSIGNMENTS,
    cacheKey: ['addon', 'assignments', addonId],
    variables: { addonId },
  });
  const found = data?.addon?.findAddonAssignments;

  const { mutateAsync: assignToAddon } = useCustomMutation({
    query: ASSIGN_TO_ADDON,
  });

  const save = (field: 'mediaIds' | 'productIds' | 'assetIds' | 'routineIds') =>
    async ({ selected }: { selected: string[] }) => {
      if (!currentWorkspace?.id) return;
      await assignToAddon({
        input: { addonId, workspaceId: currentWorkspace.id, [field]: selected },
      } as never);
      queryClient.invalidateQueries({ queryKey: ['addon'], exact: false });
    };

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !currentWorkspace?.id}
      initialTabKey={initialTab}
      onComplete={onComplete}
      tabs={[
        {
          key: 'media',
          label: t('addOn.list.media'),
          searchPlaceholder: t('addOn.assignment.mediaSearchPlaceholder'),
          assignments: found?.media,
          onSave: save('mediaIds'),
          renderList: (p) => (
            <AddonMediaAssignmentList
              workspaceId={currentWorkspace!.id}
              addonId={addonId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              mediaAssignments={p.assignments}
              onMediaAssignmentsChange={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: 'product',
          label: t('addOn.list.product'),
          searchPlaceholder: t('addOn.assignment.productSearchPlaceholder'),
          assignments: found?.products,
          onSave: save('productIds'),
          renderList: (p) => (
            <AddonProductAssignmentList
              workspaceId={currentWorkspace!.id}
              addonId={addonId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: 'assets',
          label: t('addOn.list.asset'),
          searchPlaceholder: t('addOn.assignment.assetSearchPlaceholder'),
          assignments: found?.assets,
          onSave: save('assetIds'),
          renderList: (p) => (
            <AddonAssetsAssignmentList
              workspaceId={currentWorkspace!.id}
              addonId={addonId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: 'routines',
          label: t('addOn.list.routines'),
          searchPlaceholder: t('addOn.assignment.routineSearchPlaceholder'),
          assignments: found?.routines,
          onSave: save('routineIds'),
          renderList: (p) => (
            <AddonRoutinesAssignList
              workspaceId={currentWorkspace!.id}
              addonId={addonId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};
